/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import {
    HttpClient,
    HttpContext,
    HttpErrorResponse,
    HttpHeaders,
    HttpParams,
    HttpResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { TIMEOUT } from './context';
import { ApiClientConfig } from './models/api-client.model';
import { ApiConfig } from './models/api-config.model';
import { PathParams } from './models/path-params.model';

/**
 * ApiClient service help to invoke backend api
 */
@Injectable()
export class ApiClient {
    onError$: Subject<HttpErrorResponse> = new Subject();
    private _config: ApiClientConfig;

    constructor(config: ApiClientConfig, private _httpClient: HttpClient) {
        this._config = config;
    }

    private get _timeout(): number {
        // @ts-ignore
        return this._config.getBackendConfig().timeout;
    }

    /**
     * Make a request
     * @param apiName the request's api name to invoke
     * @param body the request's body
     * @param queryParams the request's qury string params
     * @param pathParams the request's path params
     *
     * @param contextFn
     * @returns the request's observable
     */
    request<T>(
        apiName: string,
        body?: unknown,
        queryParams?: HttpParams,
        pathParams?: PathParams,
        contextFn?: ((context: HttpContext) => HttpContext)[]
    ): Observable<T> {
        const apiConfig = this._config.getApiConfig(apiName);
        // @ts-ignore
        return this._request<T>(apiConfig, body, queryParams, pathParams, contextFn).pipe(
            // @ts-ignore
            catchError((res: HttpErrorResponse) => this._handleError(res)),
            map((data: T) => this._handleSuccess<T>(data))
        );
    }

    /**
     * Make a download request
     * @param apiName the request's api name to invoke
     * @param body the request's body
     * @param queryParams the request's qury string params
     * @param pathParams the request's path params
     * @param contextFn
     * @returns the request's observable
     */
    download<T>(
        apiName: string,
        body?: unknown,
        queryParams?: HttpParams,
        pathParams?: PathParams,
        contextFn?: ((context: HttpContext) => HttpContext)[]
    ): Observable<HttpErrorResponse | HttpResponse<Blob>> {
        const apiConfig = this._config.getApiConfig(apiName);
        // @ts-ignore

        return this._getBlob(apiConfig, body, queryParams, pathParams, contextFn).pipe(
            // @ts-ignore
            catchError((res: HttpErrorResponse) => this._handleError(res))
        );
    }

    /**
     * Return the path builded
     * @param apiName the api's name
     * @param pathParams the api's pathParams
     * @returns th path compiled
     */
    getUrlByApiName(apiName: string, pathParams?: PathParams): string {
        return this._bindPathParams(
            // @ts-ignore
            this._config.getApiConfig(apiName).url,
            pathParams
        );
    }

    /**
     * Globally handle response
     * @param response response of the requst
     * @returns response
     */
    private _handleSuccess<T>(response: T): T {
        return response;
    }

    /**
     * Globally handle error response
     * @param response response of the requst
     * @returns error to return
     */
    private _handleError(response: HttpErrorResponse): Observable<HttpErrorResponse> {
        console.error(response);
        this.onError$.next(response);
        return throwError(() => response);
    }

    private _request<T>(
        apiConfig: ApiConfig,
        body?: unknown,
        queryParams?: HttpParams,
        pathParams?: PathParams,
        contextFn?: ((context: HttpContext) => HttpContext)[]
    ): Observable<T> {
        // Clone degli header per non modificare l'originale
        let headers = apiConfig.headers;

        // Se il body è FormData, rimuovi il Content-Type per permettere al browser
        // di gestire automaticamente il multipart/form-data con boundary
        if (body instanceof FormData) {
            headers = (headers as HttpHeaders)?.delete ? (headers as HttpHeaders).delete('Content-Type') : headers;
        }

        return this._httpClient.request<T>(apiConfig.method, this._bindPathParams(apiConfig.url, pathParams), {
            headers,
            observe: 'body',
            body: body || {},
            params: queryParams || null,
            responseType: 'json',
            // @ts-ignore
            context: this._getContext(contextFn, apiConfig)
        });
    }

    private _getBlob(
        apiConfig: ApiConfig,
        body?: unknown,
        queryParams?: HttpParams,
        pathParams?: PathParams,
        contextFn?: ((context: HttpContext) => HttpContext)[]
    ): Observable<HttpResponse<Blob>> {
        return this._httpClient.request(apiConfig.method, this._bindPathParams(apiConfig.url, pathParams), {
            headers: apiConfig.headers,
            observe: 'response',
            body: body || {},
            params: queryParams || null,
            responseType: 'blob',
            // @ts-ignore
            context: this._getContext(contextFn, apiConfig)
        });
    }

    private _getContext(contextFn: ((context: HttpContext) => HttpContext)[], apiConfig: ApiConfig): HttpContext {
        let context = new HttpContext();
        if (contextFn && contextFn.length) {
            contextFn.forEach((fn) => {
                context = fn(context);
            });
        }
        context = context.set(TIMEOUT, apiConfig.timeout || this._timeout || 30000);
        return context;
    }

    private _bindPathParams(url: string, params: PathParams = {}): string {
        let urlInternal = url;
        for (const key in params) {
            if (params[key]) {
                urlInternal = urlInternal.replace(new RegExp(`{${key}}`, 'g'), params[key]);
            }
        }
        return urlInternal;
    }
}
